// WindParticleLayer — screen-space trail buffer with camera-clear (v5)
//
// Architecture: classic ping-pong screen-space trail buffer.
// Per-frame: compare projection matrix to previous frame.
//   Camera still  → fadeOpacity = 0.996  → long flowing trails accumulate
//   Camera moved  → fadeOpacity = 0.0    → trail clears instantly
//
// This is geographically correct: the UPDATE shader always moves particles
// along the actual wind field at their UV position. When you look at
// California you see California winds; pan to New York and the old trails
// clear immediately, New York winds build up within ~1 second.
//
// The trail doesn't "follow" the camera because it clears on every pan.

import type {
  CustomLayerInterface,
  Map as MlMap,
  CustomRenderMethodInput,
} from 'maplibre-gl';
import { QUAD_VS, SCREEN_FS, UPDATE_FS, DRAW_VS, DRAW_FS } from './windShaders';

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src); gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? '';
    gl.deleteShader(sh);
    throw new Error(`Shader error: ${log}`);
  }
  return sh;
}
function linkProgram(gl: WebGLRenderingContext, vs: string, fs: string): WebGLProgram {
  const v = compileShader(gl, gl.VERTEX_SHADER, vs);
  const f = compileShader(gl, gl.FRAGMENT_SHADER, fs);
  const p = gl.createProgram()!;
  gl.attachShader(p, v); gl.attachShader(p, f);
  gl.linkProgram(p); gl.deleteShader(v); gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error(`Link error: ${gl.getProgramInfoLog(p)}`);
  }
  return p;
}
function createTexture(gl: WebGLRenderingContext, filter: number,
  data: HTMLImageElement | Uint8Array, w?: number, h?: number): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  if (data instanceof HTMLImageElement) {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data);
  } else {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w!, h!, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  }
  gl.bindTexture(gl.TEXTURE_2D, null);
  return t;
}
function bindAttrib(gl: WebGLRenderingContext, buf: WebGLBuffer, loc: number, size: number) {
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
}
function bindTexture(gl: WebGLRenderingContext, tex: WebGLTexture, unit: number) {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
}

export interface WindParticleLayerOptions {
  particleCount?: number;
  speedFactor?:   number;
  fadeOpacity?:   number;
  dropRate?:      number;
  dropRateBump?:  number;
  windClampMps?:  number;
}

export class WindParticleLayer implements CustomLayerInterface {
  readonly id = 'wind-particles';
  readonly type = 'custom' as const;
  readonly renderingMode = '2d' as const;

  particleCount: number;
  speedFactor:   number;
  fadeOpacity:   number;
  dropRate:      number;
  dropRateBump:  number;
  windClampMps:  number;

  private windImg:     HTMLImageElement | null = null;
  private windTexture: WebGLTexture | null = null;
  private windWidth  = 0;
  private windHeight = 0;

  private gl:  WebGLRenderingContext | null = null;
  private map: MlMap | null = null;

  private progUpdate: WebGLProgram | null = null;
  private progScreen: WebGLProgram | null = null;
  // DRAW program is per-projection-variant (mercator vs globe vs transition).
  // Cached by shaderData.variantName so we don't recompile every frame.
  private progDrawCache: Map<string, WebGLProgram> = new Map();

  private quadBuf:  WebGLBuffer | null = null;
  private indexBuf: WebGLBuffer | null = null;
  private fb:       WebGLFramebuffer | null = null;

  private stateA: WebGLTexture | null = null;
  private stateB: WebGLTexture | null = null;
  private stateRes = 0;

  private screenA: WebGLTexture | null = null;
  private screenB: WebGLTexture | null = null;
  private screenW = 0;
  private screenH = 0;

  // Resize handler is stored as a field so onRemove() can detach it.
  // The original code attached an anonymous closure that never came
  // off; each style rebuild therefore leaked a handler holding `gl`
  // and `map`. Audit 2026-05-22 CR-1.
  private resizeHandler: (() => void) | null = null;
  // Debounce timer + last-applied dimensions so we don't reallocate
  // the ~100 MB-per-side trail textures on every transient resize.
  // iPhone 13 at DPR 3 makes those textures ~106 MB EACH; the original
  // unthrottled handler could blow past the iOS Safari WebGL budget
  // (~256 MB) and trigger context loss.
  private resizeTimer: number | null = null;
  // Cap the backing buffer to this effective DPR. iPhones report DPR 3
  // but the wind layer is a coarse visual; the resolution difference
  // between 2 and 3 is imperceptible while the memory cost is
  // proportional to DPR². Falls through to 1 in tests / SSR.
  private static readonly MAX_BACKING_DPR = 2;

  // visibilitychange handler stored so onRemove() can detach it.
  private visibilityHandler: (() => void) | null = null;

  // Camera-move detection via MapLibre API (more reliable than matrix diff).

  private inited = false;

  constructor(opts: WindParticleLayerOptions = {}) {
    this.particleCount = opts.particleCount ?? 1500;
    this.speedFactor   = opts.speedFactor   ?? 0.13;  // bumped from 0.10 to offset non-linear curve's reduction at the fast end
    this.fadeOpacity   = opts.fadeOpacity   ?? 0.985;  // both RGB & alpha decay as opacity^2 then snap to zero (no snail trails)
    this.dropRate      = opts.dropRate      ?? 0.0003;
    this.dropRateBump  = opts.dropRateBump  ?? 0.0;   // keep 0 — was causing random snow
    this.windClampMps  = opts.windClampMps  ?? 100;
    this.stateRes      = Math.ceil(Math.sqrt(this.particleCount));
    this.particleCount = this.stateRes * this.stateRes;
  }

  onAdd(map: MlMap, gl: WebGLRenderingContext) {
    this.map = map; this.gl = gl;
    // Clamp the canvas backing-buffer DPR before any allocation. On
    // high-DPR phones MapLibre will otherwise create a ~3510×7596
    // drawing buffer which we'd then need two RGBA textures of for
    // the screen ping-pong. Reducing DPR to 2 cuts the per-texture
    // footprint by ~56%. Audit 2026-05-22 CR-1.
    if (typeof window !== 'undefined' && window.devicePixelRatio > WindParticleLayer.MAX_BACKING_DPR) {
      try {
        // setPixelRatio is a runtime no-op on some MapLibre builds; we
        // tolerate the failure quietly because the layer still works
        // at native DPR — it just uses more GPU memory.
        const setPixelRatio = (map as unknown as { setPixelRatio?: (n: number) => void }).setPixelRatio;
        if (typeof setPixelRatio === 'function') {
          setPixelRatio.call(map, WindParticleLayer.MAX_BACKING_DPR);
        }
      } catch { /* ignore */ }
    }
    try {
      this.progUpdate = linkProgram(gl, QUAD_VS,   UPDATE_FS);
      this.progScreen = linkProgram(gl, QUAD_VS,   SCREEN_FS);
      // progDraw is compiled lazily per projection variant on first render.
    } catch (e) { console.error('[WindParticles] shader init:', e); return; }

    this.quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0,1,0,0,1,0,1,1,0,1,1]), gl.STATIC_DRAW);

    const idx = new Float32Array(this.particleCount);
    for (let i = 0; i < this.particleCount; i++) idx[i] = i;
    this.indexBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.indexBuf);
    gl.bufferData(gl.ARRAY_BUFFER, idx, gl.STATIC_DRAW);

    // Seed particles within the current viewport so they’re visible from frame 1.
    // Falls back to global distribution if bounds aren’t available yet.
    const [vMinX, vMinY, vMaxX, vMaxY] = this.getViewportUV();
    const state = new Uint8Array(this.particleCount * 4);
    for (let i = 0; i < this.particleCount; i++) {
      const x = vMinX + Math.random() * (vMaxX - vMinX);
      const y = vMinY + Math.random() * (vMaxY - vMinY);
      state[i*4]   = Math.floor(x * 255);
      state[i*4+1] = Math.floor((x*255 - Math.floor(x*255)) * 255);
      state[i*4+2] = Math.floor(y * 255);
      state[i*4+3] = Math.floor((y*255 - Math.floor(y*255)) * 255);
    }
    this.stateA = createTexture(gl, gl.NEAREST, state, this.stateRes, this.stateRes);
    this.stateB = createTexture(gl, gl.NEAREST, state, this.stateRes, this.stateRes);
    this.fb = gl.createFramebuffer();
    this.inited = true;

    this.resizeScreenBuffers(gl);
    // Bind resize via a named handler so onRemove() can detach it.
    // The handler debounces and gates on a 5% delta to avoid the
    // every-style-rebuild texture reallocation that drove the
    // 'blank map after a few basemap switches' crash on iOS.
    this.resizeHandler = () => {
      if (this.resizeTimer != null) {
        window.clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = window.setTimeout(() => {
        this.resizeTimer = null;
        if (this.gl) this.resizeScreenBuffers(this.gl);
      }, 250);
    };
    map.on('resize', this.resizeHandler);

    // visibilitychange: bail out of expensive render work when the tab
    // is hidden; trigger a single repaint on resume so the layer
    // catches up. Audit 2026-05-22 CR-1.
    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          this.map?.triggerRepaint();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    if (this.windImg) this.setWindImage(this.windImg);
  }

  onRemove() {
    const gl = this.gl; if (!gl) return;
    [this.progUpdate, this.progScreen].forEach(p => p && gl.deleteProgram(p));
    for (const p of this.progDrawCache.values()) gl.deleteProgram(p);
    this.progDrawCache.clear();
    [this.quadBuf, this.indexBuf].forEach(b => b && gl.deleteBuffer(b));
    [this.stateA, this.stateB, this.screenA, this.screenB, this.windTexture]
      .forEach(t => t && gl.deleteTexture(t));
    if (this.fb) gl.deleteFramebuffer(this.fb);
    // Detach listeners. The original code never did this — each style
    // rebuild leaked a resize handler holding `gl` and `map`.
    if (this.resizeHandler && this.map) {
      this.map.off('resize', this.resizeHandler);
    }
    this.resizeHandler = null;
    if (this.resizeTimer != null) {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = null;
    // Null GL refs so a dangling instance can't keep textures /
    // shaders alive past unmount.
    this.progUpdate = null;
    this.progScreen = null;
    this.quadBuf = null;
    this.indexBuf = null;
    this.fb = null;
    this.stateA = null;
    this.stateB = null;
    this.screenA = null;
    this.screenB = null;
    this.windTexture = null;
    this.gl = null;
    this.map = null;
    this.inited = false;
  }

  /** Get the DRAW program for the current projection variant. */
  private getDrawProgram(gl: WebGLRenderingContext, prelude: string, variantName: string): WebGLProgram | null {
    let prog = this.progDrawCache.get(variantName);
    if (prog) return prog;
    try {
      const vs = DRAW_VS.replace('__PRELUDE__', prelude);
      prog = linkProgram(gl, vs, DRAW_FS);
      this.progDrawCache.set(variantName, prog);
      return prog;
    } catch (e) {
      console.error('[WindParticles] draw shader compile failed:', e);
      return null;
    }
  }

  setWindImage(img: HTMLImageElement) {
    const gl = this.gl;
    if (!gl || !this.inited) { this.windImg = img; return; }
    if (this.windTexture) gl.deleteTexture(this.windTexture);
    this.windImg    = img;
    this.windWidth  = img.naturalWidth  || img.width;
    this.windHeight = img.naturalHeight || img.height;
    // MapLibre sets UNPACK_FLIP_Y_WEBGL=true for its own tile textures.
    // Explicitly reset it so our PNG loads with the correct orientation
    // (row 0 = 90°N = UV y=0). Without this the texture loads upside-down
    // → we sample southern-hemisphere wind for northern-hemisphere positions
    // → directions are reversed (N→S shows as S→N, etc.).
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    this.windTexture = createTexture(gl, gl.LINEAR, img);
    this.map?.triggerRepaint();
  }

  private resizeScreenBuffers(gl: WebGLRenderingContext) {
    const w = gl.drawingBufferWidth, h = gl.drawingBufferHeight;
    // Gate: skip if exact match already in place.
    if (w === this.screenW && h === this.screenH && this.screenA) return;
    // Gate: skip on tiny deltas (< 5%) so a soft-keyboard show/hide,
    // a URL-bar collapse, or a 1px Safari layout twitch doesn't trigger
    // a ~200 MB reallocation. We still update the stored dims so the
    // logical viewport stays current.
    if (this.screenA && this.screenW > 0 && this.screenH > 0) {
      const dw = Math.abs(w - this.screenW) / this.screenW;
      const dh = Math.abs(h - this.screenH) / this.screenH;
      if (dw < 0.05 && dh < 0.05) {
        this.screenW = w; this.screenH = h;
        return;
      }
    }
    const empty = new Uint8Array(w * h * 4);
    if (this.screenA) gl.deleteTexture(this.screenA);
    if (this.screenB) gl.deleteTexture(this.screenB);
    this.screenA = createTexture(gl, gl.NEAREST, empty, w, h);
    this.screenB = createTexture(gl, gl.NEAREST, empty, w, h);
    this.screenW = w; this.screenH = h;
  }

  render(gl: WebGLRenderingContext, args: CustomRenderMethodInput) {
    if (!this.inited || !this.windTexture || !this.stateA || !this.stateB ||
        !this.screenA || !this.screenB || !this.fb ||
        !this.progUpdate || !this.progScreen ||
        !this.quadBuf || !this.indexBuf) {
      return;
    }
    // Guard against lost WebGL context. On iOS Safari / low-memory Android
    // the context can be lost on tab switch or background. The null guards
    // above won't catch this because JS handles are still non-null — only
    // isContextLost() tells us the GPU objects are dead.
    if (gl.isContextLost()) return;
    // Visibility gate: when the tab is hidden, don't burn GPU updating
    // a trail buffer no one can see. iOS keeps the WebGL context alive
    // for several minutes after backgrounding, and even rate-limited
    // repaints there drain battery and generate heat. Audit 2026-05-22
    // CR-1.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      // Note: we deliberately do NOT triggerRepaint() at the end of
      // this branch. The visibilitychange handler installed in onAdd
      // will re-prime the repaint loop on resume.
      return;
    }
    // Layout visibility gate: when the controller has set the layer to
    // visibility:'none' (e.g. the user toggled wind off), MapLibre
    // still calls render() on a custom layer — it's our job to bail.
    try {
      if (this.map?.getLayer(this.id)) {
        const vis = this.map.getLayoutProperty(this.id, 'visibility');
        if (vis === 'none') return;
      }
    } catch { /* layer may have been removed mid-frame; fall through */ }
    const sd = args.shaderData;
    if (!sd || !sd.vertexShaderPrelude) return;
    const progDraw = this.getDrawProgram(gl, sd.vertexShaderPrelude, sd.variantName);
    if (!progDraw) return;

    const vp     = gl.getParameter(gl.VIEWPORT) as Int32Array;
    const blend  = gl.isEnabled(gl.BLEND);
    const depth  = gl.isEnabled(gl.DEPTH_TEST);
    const stencil = gl.isEnabled(gl.STENCIL_TEST);

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);

    const pd = args.defaultProjectionData;


    // Use MapLibre's own movement state — more reliable than matrix diff,
    // which fires false positives during tile loads / internal MapLibre updates.
    const cameraMoving = !!(this.map?.isMoving() || this.map?.isZooming() || this.map?.isRotating());
    const effectiveFade = cameraMoving ? 0.0 : this.fadeOpacity;

    // ── 1. Render to screenA: fade previous trail + new particle dots ──────
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D, this.screenA, 0);
    gl.viewport(0, 0, this.screenW, this.screenH);
    gl.disable(gl.BLEND);

    // Fade previous trail
    gl.useProgram(this.progScreen);
    bindTexture(gl, this.screenB, 2);
    bindAttrib(gl, this.quadBuf, gl.getAttribLocation(this.progScreen, 'a_pos'), 2);
    gl.uniform1i(gl.getUniformLocation(this.progScreen, 'u_screen'),  2);
    gl.uniform1f(gl.getUniformLocation(this.progScreen, 'u_opacity'), effectiveFade);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // Draw current particle dots on top
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(progDraw);
    bindTexture(gl, this.stateA, 0);
    bindTexture(gl, this.windTexture, 1);
    bindAttrib(gl, this.indexBuf, gl.getAttribLocation(progDraw, 'a_index'), 1);
    gl.uniform1i(gl.getUniformLocation(progDraw, 'u_particles'),    0);
    gl.uniform1i(gl.getUniformLocation(progDraw, 'u_wind'),         1);
    gl.uniform1f(gl.getUniformLocation(progDraw, 'u_particles_res'), this.stateRes);
    gl.uniform2f(gl.getUniformLocation(progDraw, 'u_wind_min'), -this.windClampMps, -this.windClampMps);
    gl.uniform2f(gl.getUniformLocation(progDraw, 'u_wind_max'),  this.windClampMps,  this.windClampMps);
    // Bind MapLibre projection uniforms for projectTile().
    gl.uniformMatrix4fv(gl.getUniformLocation(progDraw, 'u_projection_matrix'), false, pd.mainMatrix as Float32Array);
    gl.uniformMatrix4fv(gl.getUniformLocation(progDraw, 'u_projection_fallback_matrix'), false, pd.fallbackMatrix as Float32Array);
    gl.uniform4f(gl.getUniformLocation(progDraw, 'u_projection_tile_mercator_coords'),
      pd.tileMercatorCoords[0], pd.tileMercatorCoords[1], pd.tileMercatorCoords[2], pd.tileMercatorCoords[3]);
    gl.uniform4f(gl.getUniformLocation(progDraw, 'u_projection_clipping_plane'),
      pd.clippingPlane[0], pd.clippingPlane[1], pd.clippingPlane[2], pd.clippingPlane[3]);
    gl.uniform1f(gl.getUniformLocation(progDraw, 'u_projection_transition'), pd.projectionTransition);
    gl.drawArrays(gl.POINTS, 0, this.particleCount);

    // ── 2. Blit screenA to map canvas ──────────────────────────────────────
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(vp[0], vp[1], vp[2], vp[3]);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.progScreen);
    bindTexture(gl, this.screenA, 2);
    bindAttrib(gl, this.quadBuf, gl.getAttribLocation(this.progScreen, 'a_pos'), 2);
    gl.uniform1i(gl.getUniformLocation(this.progScreen, 'u_screen'),  2);
    gl.uniform1f(gl.getUniformLocation(this.progScreen, 'u_opacity'), 1.0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    // ── 3. Swap screen ping-pong ───────────────────────────────────────────
    [this.screenA, this.screenB] = [this.screenB, this.screenA];

    // ── 4. Restore GL state ────────────────────────────────────────────────
    if (blend)   gl.enable(gl.BLEND);      else gl.disable(gl.BLEND);
    if (depth)   gl.enable(gl.DEPTH_TEST); else gl.disable(gl.DEPTH_TEST);
    if (stencil) gl.enable(gl.STENCIL_TEST); else gl.disable(gl.STENCIL_TEST);

    // ── 5. Advect particle positions ───────────────────────────────────────
    this.updateParticles(gl);

    this.map?.triggerRepaint();
  }

  // Returns the current viewport in wind-raster UV coordinates [0,1]².
  // UV x = (lon + 180) / 360;  UV y = (90 - lat) / 180.
  // Falls back to full-world [0,0,1,1] if bounds are unavailable.
  private getViewportUV(): [number, number, number, number] {
    if (!this.map) return [0, 0, 1, 1];
    try {
      const b = this.map.getBounds();
      const minX = Math.max(0, Math.min(1, (b.getWest()  + 180) / 360));
      const maxX = Math.max(0, Math.min(1, (b.getEast()  + 180) / 360));
      const minY = Math.max(0, Math.min(1, (90 - b.getNorth()) / 180));
      const maxY = Math.max(0, Math.min(1, (90 - b.getSouth()) / 180));
      // Ensure non-zero range (degenerate at extreme zoom)
      if (maxX - minX < 0.001 || maxY - minY < 0.001) return [0, 0, 1, 1];
      return [minX, minY, maxX, maxY];
    } catch {
      return [0, 0, 1, 1];
    }
  }

  private updateParticles(gl: WebGLRenderingContext) {
    if (!this.progUpdate || !this.quadBuf || !this.stateA || !this.stateB ||
        !this.fb || !this.windTexture) return;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D, this.stateB, 0);
    gl.viewport(0, 0, this.stateRes, this.stateRes);
    gl.disable(gl.BLEND);

    const [vpMinX, vpMinY, vpMaxX, vpMaxY] = this.getViewportUV();

    gl.useProgram(this.progUpdate);
    bindTexture(gl, this.stateA, 0);
    bindTexture(gl, this.windTexture, 1);
    bindAttrib(gl, this.quadBuf, gl.getAttribLocation(this.progUpdate, 'a_pos'), 2);
    gl.uniform1i(gl.getUniformLocation(this.progUpdate, 'u_particles'),  0);
    gl.uniform1i(gl.getUniformLocation(this.progUpdate, 'u_wind'),       1);
    gl.uniform2f(gl.getUniformLocation(this.progUpdate, 'u_wind_res'),   this.windWidth, this.windHeight);
    gl.uniform2f(gl.getUniformLocation(this.progUpdate, 'u_wind_min'), -this.windClampMps, -this.windClampMps);
    gl.uniform2f(gl.getUniformLocation(this.progUpdate, 'u_wind_max'),  this.windClampMps,  this.windClampMps);
    gl.uniform1f(gl.getUniformLocation(this.progUpdate, 'u_rand_seed'),  Math.random());
    gl.uniform1f(gl.getUniformLocation(this.progUpdate, 'u_speed_factor'), this.speedFactor);
    gl.uniform1f(gl.getUniformLocation(this.progUpdate, 'u_drop_rate'),   this.dropRate);
    gl.uniform1f(gl.getUniformLocation(this.progUpdate, 'u_drop_rate_bump'), this.dropRateBump);
    gl.uniform4f(gl.getUniformLocation(this.progUpdate, 'u_viewport_uv'), vpMinX, vpMinY, vpMaxX, vpMaxY);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    [this.stateA, this.stateB] = [this.stateB, this.stateA];
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}
